import React, { useState, useEffect } from 'react';

const TenantModal = ({ isOpen, onClose, onSave, tenant = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    is_master: false,
    max_users: 100,
    expiry_date: '',
    master_tenant_id: null,
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.name || '',
        description: tenant.description || '',
        is_active: tenant.is_active !== false,
        is_master: tenant.is_master || false,
        max_users: tenant.max_users || 100,
        expiry_date: tenant.expiry_date ? new Date(tenant.expiry_date).toISOString().split('T')[0] : '',
        master_tenant_id: tenant.master_tenant_id || null,
      });
    } else {
      // Reset form for new tenant
      setFormData({
        name: '',
        description: '',
        is_active: true,
        is_master: false,
        max_users: 100,
        expiry_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        master_tenant_id: null,
      });
    }
    setErrors({});
  }, [tenant, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tenant name is required';
    }
    if (formData.max_users && (isNaN(formData.max_users) || formData.max_users < 1)) {
      newErrors.max_users = 'Must be a positive number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      // Format data for API
      const submissionData = {
        ...formData,
        max_users: formData.max_users ? parseInt(formData.max_users, 10) : null,
        expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
      };
      onSave(submissionData);
    }
  };

  if (!isOpen) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };

  const contentStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '100%',
    padding: '20px',
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginBottom: '10px'
  };

  const buttonStyle = {
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'white',
    border: '1px solid #ddd'
  };

  return (
    <div style={modalStyle}>
      <div style={contentStyle}>
        <div style={headerStyle}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
            {tenant ? 'Edit Tenant' : 'New Tenant'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                ...inputStyle,
                borderColor: errors.name ? '#ef4444' : '#ddd'
              }}
            />
            {errors.name && (
              <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>{errors.name}</p>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              style={inputStyle}
            ></textarea>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Max Users
              </label>
              <input
                type="number"
                name="max_users"
                value={formData.max_users || ''}
                onChange={handleChange}
                min="1"
                style={{
                  ...inputStyle,
                  borderColor: errors.max_users ? '#ef4444' : '#ddd'
                }}
              />
              {errors.max_users && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '4px' }}>{errors.max_users}</p>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Expiry Date
              </label>
              <input
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: '500' }}>Active</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                name="is_master"
                checked={formData.is_master}
                onChange={handleChange}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontWeight: '500' }}>Master Tenant</span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
            <button
              type="button"
              onClick={onClose}
              style={secondaryButtonStyle}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={primaryButtonStyle}
            >
              {tenant ? 'Save Changes' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TenantModal;